import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface TableActionMenuProps {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; right: number } | null;
  children: React.ReactNode;
}

export function TableActionMenu({
  isOpen,
  onClose,
  position,
  children,
}: TableActionMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on Escape or scroll
  useEffect(() => {
    const handleEvents = (e: Event) => {
      if (
        e.type === "scroll" ||
        (e instanceof KeyboardEvent && e.key === "Escape")
      ) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("scroll", handleEvents, true); // Capture phase for all scrollables
      document.addEventListener("keydown", handleEvents);
    }

    return () => {
      window.removeEventListener("scroll", handleEvents, true);
      document.removeEventListener("keydown", handleEvents);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !position) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        ref={menuRef}
        className="fixed z-50 bg-white border border-gray-100 rounded-lg shadow-xl p-1 min-w-[160px] animate-in fade-in zoom-in duration-200"
        style={{
          top: position.top,
          right: position.right,
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
